// Deterministic, compliant marketing content generated from product data.
// Keeps the product page rich without medical/therapeutic claims.

const hashStr = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

export const productRating = (slug) => {
  const r = 4.7 + (hashStr(slug) % 3) / 10; // 4.7 - 4.9
  return Math.round(r * 10) / 10;
};

export const reviewCount = (slug) => 68 + (hashStr(slug + "rc") % 240);

const REVIEWERS = [
  "Dr. M. Callahan", "L. Petrov", "Research Lab 7", "A. Nguyen, PhD", "J. Roberts",
  "K. Sørensen", "BioLab Group", "D. Okafor", "S. Whitmore", "T. Alvarez",
  "Independent Researcher", "M. Fischer", "R. Chandra", "E. Novak",
];
const TITLES = [
  "Exactly as described", "Consistent quality", "COA matched the batch", "Reliable supplier",
  "Fast, discreet shipping", "High purity confirmed", "Will reorder", "Great lab-to-lab consistency",
  "Documentation on point", "Solid packaging",
];
const BODIES = [
  "Purity matched the certificate of analysis. Packaging was secure and clearly labeled for research handling.",
  "Ordered for an ongoing study — arrived cold-chain and lyophilized as expected. Documentation was thorough.",
  "Reconstituted cleanly with no visible particulates. Batch COA was easy to access and verify.",
  "Shipping was faster than expected and the vial labeling was precise. Exactly what our lab needs.",
  "Consistent results across two separate orders. Appreciate the transparent third-party testing.",
  "Professional from order to delivery. The identity and concentration data gave us confidence.",
  "Well-packaged, well-documented, and the reference material was clearly marked research-use-only.",
  "Quality has been dependable order after order. The COA lot-matching is a big plus for our records.",
];

export const generateReviews = (slug, count = 6) => {
  const base = hashStr(slug + "rev");
  const now = Date.now();
  return Array.from({ length: count }).map((_, i) => {
    const h = hashStr(slug + i);
    const stars = (base + i) % 7 === 0 ? 4 : 5;
    const daysAgo = 6 + ((h + i * 13) % 210);
    const date = new Date(now - daysAgo * 86400000);
    return {
      id: `${slug}-rev-${i}`,
      name: REVIEWERS[(base + i) % REVIEWERS.length],
      title: TITLES[(h + i) % TITLES.length],
      body: BODIES[(h + i * 3) % BODIES.length],
      stars,
      verified: (h + i) % 5 !== 0,
      date: date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }),
    };
  });
};

export const ratingBreakdown = (slug, rating, total) => {
  // Weighted toward 5 stars, deterministic
  const five = Math.round(total * (0.72 + ((hashStr(slug) % 8) / 100)));
  const four = Math.round(total * 0.2);
  const three = Math.max(0, Math.round(total * 0.05));
  const two = Math.max(0, Math.round(total * 0.02));
  const one = Math.max(0, total - five - four - three - two);
  return [
    { stars: 5, count: five },
    { stars: 4, count: four },
    { stars: 3, count: three },
    { stars: 2, count: two },
    { stars: 1, count: one },
  ];
};

export const sellingPoints = () => [
  { icon: "ShieldCheck", title: "Third-Party Tested", desc: "Independent lab verification" },
  { icon: "FlaskConical", title: "Verified Purity", desc: "HPLC & mass-spec confirmed" },
  { icon: "FileCheck2", title: "COA Available", desc: "Certificate per batch" },
  { icon: "Truck", title: "Fast Domestic Shipping", desc: "Cold-chain, discreet" },
  { icon: "Lock", title: "Secure Checkout", desc: "Encrypted payments" },
  { icon: "Snowflake", title: "Pharmaceutical Grade", desc: "Lyophilized & sealed" },
];

export const featureBenefits = () => [
  { icon: "ShieldCheck", feature: "Third-Party Tested", benefit: "Independent laboratory verification helps provide confidence in product identity and purity." },
  { icon: "FlaskConical", feature: "HPLC Verified", benefit: "Reverse-phase chromatography on every lot supports reproducible research conditions." },
  { icon: "FileCheck2", feature: "COA Included", benefit: "A certificate of analysis accompanies each batch for your documentation and records." },
  { icon: "Snowflake", feature: "Cold-Chain Handling", benefit: "Temperature-controlled logistics help preserve peptide integrity from our bench to yours." },
  { icon: "Boxes", feature: "Lot Traceability", benefit: "Every vial is tied to a traceable lot so results can be mapped back to a specific batch." },
  { icon: "PackageCheck", feature: "Secure Packaging", benefit: "Sealed, clearly labeled research packaging reduces handling risk in transit." },
];

export const qualitySteps = () => [
  { n: "01", title: "Synthesis to Spec", desc: "Compounds are synthesized to defined research specification." },
  { n: "02", title: "Identity Confirmation", desc: "Mass spectrometry confirms molecular identity for each lot." },
  { n: "03", title: "Purity Verification", desc: "Reverse-phase HPLC quantifies purity before release." },
  { n: "04", title: "Batch Testing", desc: "Independent third-party laboratories validate each batch." },
  { n: "05", title: "Lot Traceability", desc: "Lot numbers link every vial to its certificate of analysis." },
  { n: "06", title: "Secure Packaging", desc: "Lyophilized, sealed, and cold-chain prepared for shipping." },
];

export const whyChoose = () => [
  "Independent third-party testing on every batch",
  "Batch-matched certificates of analysis",
  "Premium sourcing and defined synthesis standards",
  "Transparent, published quality metrics",
  "Fast, discreet domestic fulfillment",
  "Secure, clearly labeled research packaging",
  "Responsive, knowledgeable customer support",
];

export const generateFaqs = (product) => {
  const name = product?.name || "this compound";
  return [
    { q: "Are your products intended for human use?", a: `No. ${name} and all products are sold strictly for laboratory and research use only. They are not drugs, dietary supplements, or intended for human or veterinary use.` },
    { q: "Is a Certificate of Analysis (COA) available?", a: "Yes. Every batch ships with a certificate of analysis documenting identity and purity. Lot numbers on each vial map directly to the corresponding COA." },
    { q: "How is purity verified?", a: "Each lot is analyzed by reverse-phase HPLC and confirmed by mass spectrometry, with independent third-party laboratory validation." },
    { q: "How should the product be stored?", a: "Store lyophilized material cold and away from light per your protocol. After reconstitution, follow standard laboratory handling for peptides." },
    { q: "How is the order packaged and shipped?", a: "Products are lyophilized, sealed, and shipped cold-chain in discreet, clearly labeled research packaging with fast domestic delivery." },
    { q: "How quickly are orders processed?", a: "In-stock orders are typically processed within one business day. You'll receive tracking as soon as your order ships." },
    { q: "What payment methods do you accept?", a: "We accept major credit/debit cards and PayPal, plus Interac e-Transfer for Canadian orders. Checkout is encrypted and secure." },
    { q: "Do you ship internationally?", a: "We focus on fast domestic fulfillment. Availability for a specific destination can be confirmed with our support team before ordering." },
    { q: "Can I get batch-specific documentation for my records?", a: "Yes. Reach out with your lot number and our team will provide the matching certificate of analysis and batch details." },
    { q: "Is my information kept secure?", a: "Payments are processed over encrypted connections and your account information is protected. We never expose payment credentials in the browser." },
    { q: "Who can I contact with technical questions?", a: "Our research support team is available by email and responds within one business day to questions about compounds, documentation, or orders." },
  ];
};

export const applyProductSeo = ({ product, rating, count, faqs }) => {
  if (!product) return () => {};
  const origin = window.location.origin;
  const url = `${origin}/products/${product.slug}`;
  const title = `${product.name} | ${product.purity || "Pharmaceutical Grade"} — Enda G1`.slice(0, 60);
  const desc = `${product.name} (${product.tagline}). ${product.purity || "High-purity"} pharmaceutical-grade, third-party tested with COA. ${product.size} — fast, discreet shipping.`.slice(0, 160);
  const image = product.image;

  document.title = title;

  const setMeta = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      el.setAttribute("data-seo", "product");
      document.head.appendChild(el);
    } else {
      el.setAttribute("data-seo", "product");
    }
    el.setAttribute("content", content);
  };

  setMeta("name", "description", desc);
  setMeta("property", "og:type", "product");
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:image", image);
  setMeta("property", "og:url", url);
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", desc);
  setMeta("name", "twitter:image", image);

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    canonical.setAttribute("data-seo", "product");
    document.head.appendChild(canonical);
  } else {
    canonical.setAttribute("data-seo", "product");
  }
  canonical.setAttribute("href", url);

  const inStock = (product.stock ?? 12) > 0;
  const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const jsonld = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: [image],
      description: desc,
      sku: product.slug,
      mpn: `ENDAG1-${product.slug.toUpperCase()}`,
      brand: { "@type": "Brand", name: "Enda G1" },
      category: product.category,
      offers: {
        "@type": "Offer",
        url,
        priceCurrency: "USD",
        price: product.price,
        priceValidUntil,
        availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
        seller: { "@type": "Organization", name: "Enda G1" },
        hasMerchantReturnPolicy: {
          "@type": "MerchantReturnPolicy",
          applicableCountry: ["CA", "US"],
          returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
        },
        shippingDetails: {
          "@type": "OfferShippingDetails",
          shippingRate: {
            "@type": "MonetaryAmount",
            value: 0,
            currency: "USD",
          },
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: ["CA", "US"],
          },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 3, unitCode: "DAY" },
            transitTime: { "@type": "QuantitativeValue", minValue: 2, maxValue: 10, unitCode: "DAY" },
          },
        },
      },
      aggregateRating: (count > 0) ? {
        "@type": "AggregateRating",
        ratingValue: rating,
        reviewCount: count,
        bestRating: 5,
        worstRating: 1,
      } : undefined,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin },
        { "@type": "ListItem", position: 2, name: "Catalog", item: `${origin}/products` },
        { "@type": "ListItem", position: 3, name: product.name, item: url },
      ],
    },
  ];

  let script = document.getElementById("product-jsonld");
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "product-jsonld";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(jsonld);

  // cleanup
  return () => {
    document
      .querySelectorAll('[data-seo="product"]')
      .forEach((el) => el.removeAttribute("content") === undefined && el.remove());
    const s = document.getElementById("product-jsonld");
    if (s) s.remove();
  };
};
